import * as React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Box,
  Breadcrumbs,
  Container,
  CssBaseline,
  Toolbar,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Button,
  TextField,
  InputAdornment,
} from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Footer from "../components/Footer";
import Navigation from "../components/Navigation";
import AdminPageHeader from "../components/AdminPageHeader";
import { useApi } from "../hooks/useApi";
import { useQuery } from "@tanstack/react-query";

function getMemberName(member) {
  return (
    member.name ||
    [member.first_name, member.last_name].filter(Boolean).join(" ") ||
    member.username ||
    "—"
  );
}

export default function CircleUsers() {
  const api = useApi();
  const navigate = useNavigate();
  const [page, setPage] = React.useState(1);
  const [search, setSearch] = React.useState("");
  const [searchInput, setSearchInput] = React.useState("");

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["circle-members", page, search],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) {
        params.set("search", search);
      } else {
        params.set("page", String(page));
        params.set("per_page", "50");
      }
      return api.get(`/admin/circle/members?${params.toString()}`);
    },
  });

  const handleSearchSubmit = (e) => {
    e?.preventDefault?.();
    setSearch(searchInput.trim());
    setPage(1);
  };

  const handleSearchClear = () => {
    setSearchInput("");
    setSearch("");
    setPage(1);
  };

  const members = data?.members ?? [];
  const pagination = {
    page: data?.page ?? page,
    per_page: data?.per_page ?? 50,
    count: data?.count ?? 0,
    has_next_page: data?.has_next_page ?? false,
  };

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <Navigation />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
            backgroundColor: (theme) => theme.palette.background.default,
          }}
        >
          <Toolbar />
          <Container maxWidth="lg" sx={{ mt: 2, mb: 4, flexGrow: 1 }}>
            <Box sx={{ mb: 2 }}>
              <Breadcrumbs aria-label="breadcrumb">
                <Typography
                  color="primary"
                  component={Link}
                  to="/dashboard"
                  sx={{
                    textDecoration: "none",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Home
                </Typography>
                <Typography
                  color="primary"
                  component={Link}
                  to="/dashboard/circle"
                  sx={{
                    textDecoration: "none",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Circle
                </Typography>
                <Typography color="text.primary" fontWeight={500}>
                  Users
                </Typography>
              </Breadcrumbs>
            </Box>

            <AdminPageHeader
              title="Circle Users"
              description="View Circle community members"
              icon={<PeopleIcon sx={{ color: "primary.main" }} />}
              children={
                <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                  <Box
                    component="form"
                    onSubmit={handleSearchSubmit}
                    sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                  >
                    <TextField
                      size="small"
                      placeholder="Search by name or email..."
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" color="action" />
                          </InputAdornment>
                        ),
                        sx: { minWidth: 220 },
                      }}
                    />
                    <Button
                      type="submit"
                      variant="outlined"
                      size="small"
                      disabled={isLoading}
                    >
                      Search
                    </Button>
                    {search && (
                      <Button
                        variant="text"
                        size="small"
                        onClick={handleSearchClear}
                        disabled={isLoading}
                      >
                        Clear
                      </Button>
                    )}
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={() => refetch()}
                    disabled={isLoading}
                  >
                    Refresh
                  </Button>
                </Box>
              }
            />

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error.message}
              </Alert>
            )}

            {isLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Paper variant="outlined" sx={{ borderRadius: 2 }}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Username</TableCell>
                        <TableCell align="right"></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {members.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                            <Typography color="text.secondary">
                              No members found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        members.map((member) => (
                          <TableRow key={member.id} hover>
                            <TableCell>
                              <Typography variant="body2" fontFamily="monospace">
                                {member.id ?? "—"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography fontWeight={500}>
                                {getMemberName(member)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {member.email ?? "—"}
                            </TableCell>
                            <TableCell>
                              {member.username ?? "—"}
                            </TableCell>
                            <TableCell align="right">
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<VisibilityIcon />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/dashboard/circle/users/${member.id}`);
                                }}
                              >
                                View
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                {pagination.count > 0 && (
                  <Box sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: "divider" }}>
                    <Typography variant="body2" color="text.secondary">
                      Page {pagination.page} • {members.length} members on this page
                      {pagination.count > 0 && ` • ${pagination.count} total`}
                    </Typography>
                  </Box>
                )}
              </Paper>
            )}
          </Container>
          <Footer />
        </Box>
      </Box>
    </>
  );
}
